/**
 * The `.rbxcloth` project format.
 *
 * Shared by the client and the Worker — the Worker validates uploads against
 * these types (entitlement enforcement is server-side; client limits are a UX
 * hint only).
 *
 * See docs/decisions/project-format.md for the reasoning. The load-bearing
 * decision: **colour never appears literally in a layer.** Layers reference a
 * ColourSlotId, and a colourway is an override map. Without that, the colourway
 * generator and rank builder cannot exist.
 */

/**
 * Bump with every breaking change, and add a migration.
 *
 * **v2 — the layer model.** See docs/decisions/layer-model.md. Seven layer types
 * became three, and placements became face-relative.
 *
 * Two things about changing this number, both learned the hard way rather than
 * discovered later:
 *
 *   **Deploy the Worker first.** `unpack()` rejects forward only — an archive
 *   claiming a version above this one throws, which `saveVersion` maps to
 *   400 bad_archive. Ship a v2 client against a v1 Worker and *every save fails
 *   for everyone*. Worker first, or both together. Never client first.
 *
 *   **Reading backwards is silent.** A v2 build opens a v1 archive without
 *   complaint, because `1 > 2` is false. It is JSON: nothing checks at runtime
 *   that the objects inside match the union they are typed as, so a v1 project
 *   deserialises into something shaped like the old schema and wearing the new
 *   type. That is why `migrateLayers()` below exists and why it is keyed off the
 *   manifest rather than off the data's shape.
 */
export const FORMAT_VERSION = 2;

/**
 * The version that lets one design hold two garments — **not yet the default.**
 *
 * `FORMAT_VERSION` is deliberately still 2. The archive reader and writer below
 * already understand `garments/`, but nothing may *emit* it until the production
 * Worker accepts v3, because of the first rule above: a v3 client against a v2
 * Worker fails every save for everyone.
 *
 * `packProject` enforces that rather than trusting anyone to remember —
 * declaring a second garment while the manifest still claims v2 throws. So the
 * switch is one line here, made together with the Worker deploy, and until then
 * the unsafe combination cannot be written by accident.
 */
export const FORMAT_VERSION_OUTFIT = 3;

export const CANVAS = { width: 585, height: 559 } as const;

// ------------------------------------------------------------------ regions

/**
 * The 18 face regions. Must stay in sync with
 * assets/reference/template-regions.json — validated at startup by
 * assertRegionsMatch().
 */
/**
 * A dirty rectangle clipped to a surface, expanded to whole texels.
 *
 * Callers hand us rects derived from brush geometry, which routinely run off
 * the canvas — a brush at the left edge produces a negative x. WebGL's
 * texSubImage2D rejects those with GL_INVALID_VALUE, which drops the upload and
 * leaves the 3D preview stale while only whispering about it in the console.
 *
 * Returns null when nothing survives the clip, so callers skip the upload
 * instead of issuing a zero-area one.
 */
export function clampRect(
  rect: { x: number; y: number; w: number; h: number },
  width: number,
  height: number,
): { x: number; y: number; w: number; h: number } | null {
  // Expand outward before clipping: a rect covering half of texel 3 must upload
  // all of texel 3, or the brush leaves a hairline of stale pixels behind.
  const x0 = Math.max(0, Math.floor(rect.x));
  const y0 = Math.max(0, Math.floor(rect.y));
  const x1 = Math.min(width, Math.ceil(rect.x + rect.w));
  const y1 = Math.min(height, Math.ceil(rect.y + rect.h));
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

export const REGION_IDS = [
  "torso.up",
  "torso.right",
  "torso.front",
  "torso.left",
  "torso.back",
  "torso.down",
  "rightLimb.up",
  "rightLimb.left",
  "rightLimb.back",
  "rightLimb.right",
  "rightLimb.front",
  "rightLimb.down",
  "leftLimb.up",
  "leftLimb.front",
  "leftLimb.left",
  "leftLimb.back",
  "leftLimb.right",
  "leftLimb.down",
] as const;

export type RegionId = (typeof REGION_IDS)[number];

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Regions occluded on a normally-posed avatar. These are the good hiding places
 * for a signature watermark — measured, not guessed.
 */
export const OCCLUDED_REGIONS: readonly RegionId[] = [
  "torso.up", // under the head
  "torso.down", // under the legs
  "rightLimb.up",
  "leftLimb.up",
  "rightLimb.down", // sole of foot / palm of hand
  "leftLimb.down",
];

// ------------------------------------------------------------------ colour

export type ColourSlotId = string;

/** `#RRGGBB` or `#RRGGBBAA`. */
export type Hex = string;

export interface ColourSlot {
  name: string;
  colour: Hex;
}

export interface Palette {
  slots: Record<ColourSlotId, ColourSlot>;
}

/** A named recolour. The whole Studio tier is built on this one shape. */
export interface Colourway {
  id: string;
  name: string;
  overrides: Record<ColourSlotId, Hex>;
}

// ------------------------------------------------------------------ layers

/**
 * How a layer combines with what is under it.
 *
 * Six, out of the sixteen canvas offers, and the cut is not arbitrary: every one
 * of these is named by name in a Roblox clothing shading tutorial. Overlay does
 * the box shading and the wrinkles, Multiply the shadows and any pattern laid
 * over colour, Screen and Add the highlights, Soft light the gentler version of
 * Overlay. Hue, saturation, luminosity and difference have no use on a garment
 * and would be four more things to scroll past.
 *
 * The order here is the order the picker shows: Normal, then the darkening
 * modes, then the lightening ones. Alphabetical would put Add above Multiply
 * and split the two that lighten.
 */
export type BlendMode = "normal" | "multiply" | "overlay" | "soft-light" | "screen" | "add";

/** Every mode, in picker order. */
export const BLEND_MODES: readonly BlendMode[] = [
  "normal",
  "multiply",
  "overlay",
  "soft-light",
  "screen",
  "add",
] as const;

/** What the picker calls each one. Sentence case, like every other label. */
export const BLEND_MODE_LABELS: Record<BlendMode, string> = {
  normal: "Normal",
  multiply: "Multiply",
  overlay: "Overlay",
  "soft-light": "Soft light",
  screen: "Screen",
  add: "Add",
};

/**
 * Whether a stored value is a mode this build knows.
 *
 * A saved project carries the mode as a string, and a file written by a newer
 * build can name one this one has never heard of. Falling back to `normal` is
 * the only safe answer: refusing to open the project loses the whole design over
 * one layer's appearance, and trusting it hands an unknown string straight to
 * `globalCompositeOperation`, where it is silently ignored and the layer draws
 * normally anyway — but with nothing recorded to say so.
 */
export function isBlendMode(value: unknown): value is BlendMode {
  return typeof value === "string" && (BLEND_MODES as readonly string[]).includes(value);
}

/**
 * What every layer has, whatever its kind.
 *
 * `locked` is gone: one checkbox guarding against a problem undo already solves.
 * `clipToRegions` is gone as a stored field — it is derived from the layer's
 * kind and placement, and was never something a human should type.
 */
export interface LayerCommon {
  id: string;
  name: string;
  visible: boolean;
  /** 0..1 */
  opacity: number;
  blendMode: BlendMode;
  /**
   * Show this layer only where the one below it has pixels.
   *
   * A clipping mask, and the reason it matters more than it looks: canvas
   * composites `source-over` wherever the backdrop is empty, so a Multiply or
   * Overlay layer over nothing paints its own colour at full strength instead
   * of darkening nothing. This project shipped exactly that bug once — see
   * shading-retired.md — and clipping is what guarantees a non-empty backdrop.
   *
   * Optional, so every project written before this field keeps parsing and
   * behaves exactly as it did.
   */
  clipToBelow?: boolean;
  /**
   * Show this layer on these faces only — "Only these faces" on the layer row.
   *
   * **Absent or empty means every face**, which is what makes this additive:
   * every project written before the field existed reads back as unrestricted,
   * which is exactly what it was. No version bump, because there is no version
   * at which the old meaning and the new one disagree.
   *
   * The counterpart to the editor's face SELECTION, and deliberately a
   * different thing. A selection is where you are working now — a tool mode
   * that survives no reload and reaches no file. This is a property of the
   * artwork: the sleeves fabric stays on the sleeves whichever tool is up, and
   * it is what somebody means by "only on the sleeves".
   *
   * v1 carried this as `clipToRegions` and it was cut for being something no
   * human should type. It is the same data; what changed is that you now arrive
   * at it by picking faces and pinning them, rather than by writing region ids.
   */
  faces?: RegionId[];
}

/** A layer following a file on disk, so a save in Photoshop lands here. */
export interface FileLink {
  /** For display only — a handle cannot be serialised, so reconnect is manual. */
  filename: string;
}

/**
 * Where a placement sits — against a **face**, never against the canvas.
 *
 * This is the load-bearing decision of the whole model, so the reasoning is here
 * rather than only in the doc. The obvious alternative stores absolute template
 * pixels, and it is wrong in five ways at once:
 *
 *   - a logo can straddle the seam between two faces, putting half of it on the
 *     chest and half on the side. Here it **cannot**, by construction.
 *   - a logo can land in the 54.9% of the canvas that never renders. Here that
 *     area is not addressable at all.
 *   - "move it to the back" becomes coordinate arithmetic instead of one field.
 *   - mirroring to the paired sleeve becomes geometry instead of one field.
 *   - the panel reads "x: 340, y: 187" instead of "on the Chest".
 *
 * Rotation is included and costs less than it looks: the face rect is applied as
 * a clip *before* the rotation, so a rotated placement stays inside its face at
 * any angle. What rotation changes is the warning — the failure mode becomes
 * "visibly cut off at the face edge" rather than "straddling", which is a better
 * check because it catches oversized unrotated placements too.
 */
export interface FacePlacement {
  face: RegionId;
  /** 0..1 within the face. 0.5, 0.5 is centred. */
  x: number;
  y: number;
  /** Fraction of the face's width. 1 spans it edge to edge. */
  scale: number;
  /** Radians about the placement's own centre. Degrees are a UI concern. */
  rotation: number;
}

/** Kept alongside the rendered pixels so text stays editable after placement. */
export interface TextSpec {
  text: string;
  fontFamily: string;
  fontSize: number;
  letterSpacing: number;
  slot: ColourSlotId;
}

/**
 * Pixels across the whole template — an import, or what you painted.
 *
 * There can be several. That is deliberate, and it forced a good default: an
 * import **adds** an artwork layer rather than replacing one, so nothing anyone
 * made is ever silently destroyed. It also means a `link` belongs to a *layer*
 * rather than to the document — save in Photoshop and that layer updates while
 * everything stacked above it stays exactly where it is.
 */
/**
 * What a layer was filled with, when its pixels came from the fabric library.
 *
 * Kept so a fill stays editable across a reload: the colour dot on the layer
 * row reads this, and re-tinting or re-scaling the weave regenerates the pixels
 * from it. Without it a reopened design is a picture of a fabric rather than a
 * fabric, and the only way to change your mind is to fill again.
 *
 * **No change token here, deliberately.** The runtime record carries the
 * layer's `rev` so it can tell whether the pixels are still the ones the fill
 * produced — see src/app/fabric-source.ts. That number is meaningless across a
 * reload: revs restart, so a stored one would either collide by accident and
 * offer to recolour a layer that has been painted on, or never match and hide
 * the dot forever. The durable part is what the fill *was*; whether it still
 * describes the pixels is a question only a live session can answer, and
 * hydrate answers it by stamping the rev the restored layer actually gets.
 */
export interface FabricFill {
  /** Asset id, e.g. "gingham_check". */
  id: string;
  /** Display name at the time of the fill, so a renamed layer still reads right. */
  name: string;
  /** Tile width in template pixels. */
  tilePx: number;
  /** Applied tint, or null for the fabric as it comes. */
  tint: string | null;
}

export interface ArtworkLayer extends LayerCommon {
  kind: "artwork";
  /** layers/<id>.png */
  pixelsKey: string;
  /** Slot remap for palette-driven recolouring of flat artwork. */
  paletteMap?: Record<Hex, ColourSlotId>;
  link?: FileLink;
  /** Set when these pixels came from the fabric library. */
  fabric?: FabricFill;
}

/**
 * One image on one face: a logo, a patch, a badge, a watermark, or text.
 *
 * Text is not a fourth kind. A text layer is a picture of some words on a face,
 * so it stores the same pixels as any placement and keeps `text` alongside for
 * re-editing. That keeps compositing and export uniform — everything is a
 * bitmap drawn into a face — instead of branching on layer type in the hot path.
 */
export interface PlacementLayer extends LayerCommon {
  kind: "placement";
  placement: FacePlacement;
  /** layers/<id>.png — always present, even for text. */
  pixelsKey: string;
  paletteMap?: Record<Hex, ColourSlotId>;
  link?: FileLink;
  /** Present when the pixels were rendered from text and can be re-edited. */
  text?: TextSpec;
}

/** Prebaked ambient occlusion for a body. Blocked on the Studio capture — the
 *  kind exists now so landing it later is not a format change. */
export interface ShadingLayer extends LayerCommon {
  kind: "shading";
  bodyType: string;
  /** 0..1 */
  intensity: number;
  tintSlot?: ColourSlotId;
}

export type Layer = ArtworkLayer | PlacementLayer | ShadingLayer;

export type LayerKind = Layer["kind"];

/**
 * The discriminator is `kind`, and v1's was `type`.
 *
 * That rename is not cosmetic. Reading an old archive is silent — `1 > 2` is
 * false, so `unpack` lets it through, and JSON carries no runtime type
 * information to catch the mismatch. Because the discriminator moved, a v1 layer
 * fails this guard instead of sailing on as a `Layer` that has no `.kind` and no
 * `.placement`. A wrong archive now fails loudly at the boundary rather than
 * three call frames later.
 */
export function isLayer(value: unknown): value is Layer {
  if (typeof value !== "object" || value === null) return false;
  const kind = (value as { kind?: unknown }).kind;
  return kind === "artwork" || kind === "placement" || kind === "shading";
}

/**
 * Recolourability is a property of the layer's data, not of its type.
 *
 * v1 answered this with a list of blessed layer types, which under import-first
 * would have marked every real project un-recolourable — imported artwork was a
 * `raster` layer, and `raster` was not on the list. What actually decides it is
 * whether the layer carries a palette map.
 */
export function isRecolourable(layer: Layer): boolean {
  // Shading multiplies; it is colour-neutral and never blocks a colourway.
  if (layer.kind === "shading") return true;
  return layer.paletteMap !== undefined && Object.keys(layer.paletteMap).length > 0;
}

// ----------------------------------------------------------------- project

export type GarmentType = "shirt" | "pants" | "tshirt";

/**
 * Every garment the format knows, as data.
 *
 * `satisfies Record<GarmentType, true>` makes the compiler the thing that keeps
 * this list complete: adding a member to the union without adding it here stops
 * the build, rather than producing a validator that silently rejects the new
 * one everywhere it is used.
 */
export const GARMENT_TYPES = Object.keys({
  shirt: true,
  pants: true,
  tshirt: true,
} satisfies Record<GarmentType, true>) as GarmentType[];

/**
 * What each garment is called on screen.
 *
 * Beside the type rather than in a component, for the same reason `TIER_NAMES`
 * lives beside `Tier`: the export step, the designs list and the editor all
 * name these, and three inline ternaries are three places to disagree.
 */
export const GARMENT_NAMES: Record<GarmentType, string> = {
  shirt: "Shirt",
  pants: "Pants",
  tshirt: "T-shirt",
};

/**
 * A garment, or nothing.
 *
 * Needed on both sides of the wire: the create route took whatever string a
 * client sent straight into the database, and the archive a save uploads is
 * user-authored JSON.
 */
export function asGarmentType(raw: unknown): GarmentType | null {
  return typeof raw === "string" && (GARMENT_TYPES as string[]).includes(raw)
    ? (raw as GarmentType)
    : null;
}

export interface Manifest {
  formatVersion: number;
  /**
   * The **primary** garment: the one `Project.layers` and `Project.edge` carry.
   *
   * Still required, and still singular, on a format that can now hold two. A
   * design has a primary because something has to answer "what is this?" for a
   * list row, a filename stem and a v2 reader, and "shirt and pants" is not an
   * answer any of those can use.
   */
  garmentType: GarmentType;
  /**
   * Every garment in this design, primary first. Absent on v2 archives, where
   * `garmentType` alone is the whole truth.
   *
   * Denormalised from `Project.garments` on purpose: `peekManifest` reads only
   * the small entries so the project list and the Worker never unzip bitmaps,
   * and "does this design have pants" has to be answerable from there.
   */
  garments?: GarmentType[];
  canvas: { width: number; height: number };
  title: string;
  createdAt: number;
  modifiedAt: number;
  appVersion: string;
}

/**
 * Transparency is a first-class technique in Roblox clothing, not an edge case:
 * making a sleeve region transparent is how you get short sleeves, because the
 * garment composites over the body and transparency reveals skin.
 *
 * So alpha hardening cannot simply default to on.
 *   auto   — harden only regions that are already essentially binary, leaving
 *            gradients and cut-outs alone. Correct for almost everyone.
 *   always — force binary edges. For pixel-art styles that want hard cuts.
 *   never  — for deliberately sheer garments.
 */
export type AlphaHardening = "auto" | "always" | "never";

export interface EdgeSettings {
  alphaHardening: AlphaHardening;
  /** Pipeline stage 2 — needs the verified face-adjacency table. */
  seamBleed: boolean;
  /**
   * Pipeline stage 2b — reconcile the two sides of a fold so the seam stops
   * showing.
   *
   * Separate from `seamBleed` because it is a different promise. That stage
   * only writes texels with no alpha, so it can add a fringe and never alter a
   * design; this one moves painted texels toward their partner across the edge,
   * which is the only thing that removes a visible seam once the padding is
   * already correct — and is not something to do to somebody's pixels without
   * a switch they can find.
   */
  seamBlend: boolean;
  /** Gutter is 2px; one extra pass covers half-pixel sampling at the far edge. */
  dilationPasses: number;
  /**
   * Passes of RGB-only bleed underneath transparent pixels. Without this,
   * filtering between an opaque pixel and a transparent one interpolates
   * toward whatever colour happens to sit under the transparency — usually
   * black — producing a dark halo around every cut-out.
   */
  colourBleedPasses: number;
}

export const DEFAULT_EDGE_SETTINGS: EdgeSettings = {
  alphaHardening: "auto",
  seamBleed: true,
  seamBlend: true,
  dilationPasses: 3,
  colourBleedPasses: 4,
};

/** Preview-only settings. Not part of the exported garment. */
export interface PreviewSettings {
  /**
   * Body colour rendered underneath the garment, so transparent areas read
   * correctly while designing instead of being guessed at.
   */
  skinTone: Hex;
  bodyType: string;
}

export const DEFAULT_PREVIEW: PreviewSettings = {
  skinTone: "#D8B196",
  bodyType: "classic-r6",
};

/**
 * Roblox's standard body colours. Designers need to check a transparent design
 * against more than one, because a cut-out that reads well on a light tone can
 * disappear on a dark one.
 */
export const SKIN_TONES: ReadonlyArray<{ name: string; hex: Hex }> = [
  { name: "Pastel Brown", hex: "#FFCB98" },
  { name: "Light Orange", hex: "#F8C58C" },
  { name: "Nougat", hex: "#CB8442" },
  { name: "Brick Yellow", hex: "#D7C59A" },
  { name: "Dirt Brown", hex: "#7B5D41" },
  { name: "Reddish Brown", hex: "#69453B" },
  { name: "Bright Yellow", hex: "#F5CD30" },
  { name: "Pastel Yellow", hex: "#FFF67B" },
  { name: "White", hex: "#F2F3F3" },
  { name: "Really Black", hex: "#1B2A35" },
];

// ---------------------------------------------------------------- variants

/**
 * A rank set is a layer swap, not a recolour.
 *
 * "Corporal" and "Sergeant" are not two colours of the same thing, so palette
 * mapping — which handles colourways — structurally cannot produce a rank set.
 * Swapping one placement's pixels can.
 *
 * A variant is a **sparse override on top of the base project**: nothing is
 * duplicated, so editing the shirt updates all eight ranks at once. That is the
 * actual pain being solved, and it is the reason this is a format concept rather
 * than an export-time one.
 */
export interface VariantOverride {
  /** layers/<key>.png — swap this layer's pixels. */
  pixelsKey?: string;
  visible?: boolean;
  placement?: Partial<FacePlacement>;
}

export interface Variant {
  id: string;
  name: string;
  /** Keyed by layer id. Absent layers are inherited from the base unchanged. */
  overrides: Record<string, VariantOverride>;
}

/**
 * Count caps, not just byte caps.
 *
 * `layers.json` is attacker-controlled, and bounding total bytes does not bound
 * element count — a few MB of `{}` is a very large array. `MAX_LAYERS` already
 * exists in the Worker for exactly this reason; `variants` is a second unbounded
 * array and needs the same treatment in `peekManifest`, or it is a memory
 * exhaustion vector wearing a feature's clothes.
 *
 * 256 is well above any real rank set while keeping the parse bounded.
 */
export const MAX_VARIANTS = 256;

// ---------------------------------------------------------------- project

/**
 * One garment's pixels and edge settings.
 *
 * Layers are per garment because they are pixels on a specific template; edge
 * settings are per garment because alpha hardening on a sheer top has nothing to
 * say about the pants. Everything genuinely shared — `palette`, `colourways`,
 * `guides` — stays on the Project, which is what makes an outfit's colours match
 * without anybody synchronising anything.
 */
export interface GarmentDoc {
  layers: Layer[]; // index 0 = bottom
  edge: EdgeSettings;
}

/**
 * A horizontal rule at one template row, drawn across the torso.
 *
 * A row is the whole coordinate: the four torso side faces share y 74–202 in the
 * measured map, so one row *is* one height on the body, all the way around and
 * on both garments. See docs/decisions/outfit-plan.md §1 — that is why there is
 * no stud-space here, and no per-garment guide list.
 */
export interface Guide {
  id: string;
  /** Template row. Meaningful inside the torso strip only. */
  row: number;
  label?: string;
}

/** The in-memory project. On disk this is split across the zip entries. */
export interface Project {
  manifest: Manifest;
  palette: Palette;
  layers: Layer[]; // index 0 = bottom
  colourways: Colourway[];
  /** Absent means none. Optional so a plain garment carries no empty array and
   *  so every existing Project literal keeps compiling. */
  variants?: Variant[];
  edge: EdgeSettings;
  /**
   * Every garment in this design. Absent on v2, where the design is one garment
   * and `layers`/`edge` are all there is.
   *
   * **`layers` and `edge` above are the primary garment's, by reference.**
   * `unpackProject` assigns the same array object to both, so they cannot drift
   * — mutating `project.layers` mutates `garments[primary].layers` because they
   * are one object, not two copies. Reassigning `project.layers` wholesale would
   * break the link, and nothing does that today.
   *
   * The duplication is transitional. It exists so the ~100 existing readers of
   * `project.layers` keep working while the runtime learns to hold two stacks;
   * it goes away when they read `garments` directly. See
   * docs/decisions/outfit-implementation.md §1.
   */
  garments?: Partial<Record<GarmentType, GarmentDoc>>;
  /** Project-level: a guide is a fact about the body, not about a garment. */
  guides?: Guide[];
}

// ------------------------------------------------------------- migration

/** What a v1 layer looked like. Only the fields the migration reads. */
interface LegacyLayer {
  type?: string;
  id?: string;
  name?: string;
  visible?: boolean;
  opacity?: number;
  pixelsKey?: string;
  sourceKey?: string;
  bodyType?: string;
  intensity?: number;
  tintSlot?: string;
  paletteMap?: Record<Hex, ColourSlotId>;
}

export interface Migration {
  layers: Layer[];
  /** Anything that could not be carried across, for the UI to report honestly. */
  dropped: string[];
}

/**
 * Upgrade layers from an older archive.
 *
 * Keyed off `manifest.formatVersion`, never off the data's shape, because the
 * shape is exactly what cannot be trusted: a v1 archive deserialises without
 * complaint into objects typed as the current union.
 *
 * v1 → v2 is lossy on purpose. `fill`, `pattern`, `text` and `watermark` were
 * generators that no build ever rendered — there are no pixels to carry across,
 * only parameters for features that were never implemented. Dropping them and
 * *saying so* beats materialising layers that would render as nothing.
 */
export function migrateLayers(formatVersion: number, raw: unknown): Migration {
  if (!Array.isArray(raw)) throw new Error("layers.json is not an array");

  /*
   * v2 and v3 share one layer shape.
   *
   * v3 changed the *container* — one archive may now hold two garments — and
   * left a layer exactly as it was. So there is no v2 → v3 layer migration to
   * write, and treating v3 as "not current" would make every v3 archive
   * unreadable for no reason.
   *
   * Kept as a range rather than a second equality test so that the next format
   * bump has to come here and state which it is: a container change, or a layer
   * change that needs a migration.
   */
  if (formatVersion >= FORMAT_VERSION && formatVersion <= FORMAT_VERSION_OUTFIT) {
    const bad = raw.findIndex((l) => !isLayer(l));
    if (bad !== -1) {
      throw new Error(`layers[${bad}] is not a v${FORMAT_VERSION} layer`);
    }
    return { layers: raw as Layer[], dropped: [] };
  }

  if (formatVersion > FORMAT_VERSION_OUTFIT) {
    throw new Error(`cannot migrate down from v${formatVersion} to v${FORMAT_VERSION_OUTFIT}`);
  }

  if (formatVersion !== 1) throw new Error(`no migration from format v${formatVersion}`);

  const layers: Layer[] = [];
  const dropped: string[] = [];

  for (const [i, entry] of (raw as LegacyLayer[]).entries()) {
    const common = {
      id: entry.id ?? `migrated-${i}`,
      name: entry.name ?? `Layer ${i + 1}`,
      visible: entry.visible ?? true,
      opacity: entry.opacity ?? 1,
      // v1 blend modes beyond these two have no equivalent; normal is the only
      // honest fallback, and silently applying "overlay" would be worse.
      blendMode: "normal" as const,
    };

    switch (entry.type) {
      // Both were full-canvas pixels, which is exactly what artwork is now.
      case "raster":
      case "image": {
        const pixelsKey = entry.pixelsKey ?? entry.sourceKey;
        if (!pixelsKey) {
          dropped.push(`${common.name} (${entry.type} with no pixels)`);
          break;
        }
        layers.push({
          ...common,
          kind: "artwork",
          pixelsKey,
          ...(entry.paletteMap ? { paletteMap: entry.paletteMap } : {}),
        });
        break;
      }
      case "shading":
        layers.push({
          ...common,
          kind: "shading",
          bodyType: entry.bodyType ?? "r6-classic",
          intensity: entry.intensity ?? 1,
          ...(entry.tintSlot ? { tintSlot: entry.tintSlot } : {}),
        });
        break;
      default:
        dropped.push(`${common.name} (${entry.type ?? "unknown"} is not supported in v2)`);
    }
  }

  return { layers, dropped };
}

// ------------------------------------------------------------- validation

/**
 * True when every layer's colour can be driven from the palette — i.e. the
 * colourway generator and rank builder will produce correct output. Surfaced
 * in the UI so a designer learns *before* finishing that a raster layer has
 * opted them out of variants.
 */
export function supportsColourways(project: Project): boolean {
  return project.layers.every(isRecolourable);
}

/** Guards against the region list drifting from template-regions.json. */
export function assertRegionsMatch(regionMap: Record<string, Rect>): void {
  const fromMap = Object.keys(regionMap)
    .filter((k) => !k.startsWith("$"))
    .sort();
  const known = [...REGION_IDS].sort();
  if (fromMap.length !== known.length || fromMap.some((k, i) => k !== known[i])) {
    throw new Error(
      `Region map drift: template-regions.json has [${fromMap}] but format.ts declares [${known}]`,
    );
  }
}
